import { useList, useDelete, useUpdate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FormHeader } from "@/components/forms";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import { DataTable, TableToolbar, FilterSelect, type Column, type SortState, type BulkAction } from "@/components/tables";
import { StatusBadge, getActiveStatus } from "@/components/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Zap,
  Clock,
} from "lucide-react";
import { type Trigger, TRIGGER_ACTION_OPTIONS, TRIGGER_EVENT_OPTIONS } from './types';
import { getEventTypeLabel } from '@/lib/translations';
import { notify } from '@/lib/notify';

const getActionLabel = (type: string): string => {
  return TRIGGER_ACTION_OPTIONS.find(o => o.value === type)?.label ?? type;
};

/**
 * Triggers list page with DataTable
 */
export function TriggersList() {
  useScrollRestoration();
  const navigate = useNavigate();
  const [page, setPage] = usePersistedState("triggers:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("triggers:sort", { field: "name", order: "asc" });
  const [deleteItems, setDeleteItems] = useState<Trigger[]>([]);
  const [search, setSearch] = usePersistedState("triggers:search", "");
  const [filterEventType, setFilterEventType] = usePersistedState<string>("triggers:filterEventType", "all");
  const [filterActive, setFilterActive] = usePersistedState<string>("triggers:filterActive", "all");
  const pageSize = 20;

  const filters = [
    ...(search ? [{ field: "search", operator: "contains" as const, value: search }] : []),
    ...(filterEventType !== "all" ? [{ field: "eventType", operator: "eq" as const, value: filterEventType }] : []),
    ...(filterActive !== "all" ? [{ field: "isActive", operator: "eq" as const, value: filterActive === "active" }] : []),
  ];

  const { result, query } = useList<Trigger>({
    resource: "triggers",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const { mutate: deleteTrigger } = useDelete();
  const { mutate: updateTrigger } = useUpdate();

  const triggers = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query?.isLoading ?? false;

  const handleToggleActive = (items: Trigger[]) => {
    if (items.length === 0) {return;}
    for (const item of items) {
      updateTrigger({
        resource: "triggers",
        id: item.id,
        values: { isActive: !item.isActive },
        successNotification: false,
      });
    }
    notify.success(`Обновлено: ${items.length}`);
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) {return;}

    for (const item of deleteItems) {
      deleteTrigger({ resource: "triggers", id: item.id, successNotification: false });
    }
    notify.success(`Удалено: ${deleteItems.length}`);
    setDeleteItems([]);
  };

  const columns: Column<Trigger>[] = [
    {
      key: "name",
      header: "Триггер",
      sortable: true,
      render: (_, t) => (
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-yellow-500" />
          <span className="font-medium">{t.name}</span>
        </div>
      ),
    },
    {
      key: "eventType",
      header: "Событие",
      sortable: true,
      render: (value) => (
        <Badge variant="outline">
          {getEventTypeLabel(String(value))}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Действия",
      render: (_, t) => {
        const actions = Array.isArray(t.actions) ? t.actions : [];
        return (
          <div className="flex flex-wrap gap-1">
            {actions.slice(0, 3).map((action, i) => (
              <Badge key={`${action.type}-${i}`} variant="outline" className="text-xs">
                {getActionLabel(action.type)}
              </Badge>
            ))}
            {actions.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{actions.length - 3}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "delay",
      header: "Задержка",
      sortable: true,
      width: "100px",
      render: (value) => {
        const delay = value as number | null;
        return delay && delay > 0 ? (
          <span className="flex items-center justify-center gap-1">
            <Clock className="h-3 w-3" />
            {delay} мин
          </span>
        ) : (
          <span className="text-muted-foreground text-center block">—</span>
        );
      },
    },
    {
      key: "isActive",
      header: "Статус",
      sortable: true,
      width: "120px",
      render: (_, t) => (
        <StatusBadge status={getActiveStatus(t.isActive)} />
      ),
    },
  ];

  const bulkActions: BulkAction<Trigger>[] = [
    {
      label: "Редактировать",
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          navigate(`/triggers/edit/${items[0].id}`);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Активировать",
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter(i => !i.isActive)),
      showOnlyWhenSelected: true,
    },
    {
      label: "Деактивировать",
      icon: <EyeOff className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter(i => i.isActive)),
      variant: "outline",
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Триггеры"
        description="Автоматические действия при событиях в системе"
        actions={
          <Button onClick={() => navigate("/triggers/create")}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить
          </Button>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Поиск по названию..."
        filtersCount={[filterEventType, filterActive].filter(v => v !== "all").length}
        onResetFilters={() => {
          setFilterEventType("all");
          setFilterActive("all");
          setPage(1);
        }}
        filters={
          <>
            <FilterSelect
              label="Событие"
              value={filterEventType}
              onChange={(v) => { setFilterEventType(v); setPage(1); }}
              options={TRIGGER_EVENT_OPTIONS}
              allLabel="Все события"
            />
            <FilterSelect
              label="Статус"
              value={filterActive}
              onChange={(v) => { setFilterActive(v); setPage(1); }}
              options={[
                { value: "active", label: "Активные" },
                { value: "inactive", label: "Неактивные" },
              ]}
              allLabel="Все"
            />
          </>
        }
      />

      <DataTable
        columns={columns}
        data={triggers}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(t) => navigate(`/triggers/edit/${t.id}`)}
        emptyMessage="Триггеры не найдены"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? "Удалить триггер?"
                : `Удалить ${deleteItems.length} триггеров?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить триггер «{deleteItems[0].name}»?
                  Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} триггеров?
                  Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
